<template>
  <GridComponent
    id="personList"
    ref="_grid"
    :data-source="_dataManager1"
    :allow-paging="true"
    :allow-sorting="true"
    width="auto"
    :allow-reordering="true"
    :allow-resizing="true"
    :show-column-menu="false"
    :allow-text-wrap="true"
    :row-selected="onRowSelected"
    :row-deselected="onRowDeselected"
    :page-settings="_pageSettings"
    :action-complete="onActionComplete"
    :action-failure="gridActionFailure"
  >
    <ColumnsDirective>
      <ColumnDirective
        header-text=""
        :template="'cTemplate'"
        width="70"
        :allow-sorting="false"
      >
        <template #cTemplate="{data}">
          <SogIcon
            v-show="data.insuranceId"
            icon="mdi-car"
          />
          <span
            v-show="data.hasSuggestion"
            :class="['confidence', 'confidence-' + data.suggestionConfidence]"
            :title="data.suggestionText || 'Es gibt Verknüpfungsvorschläge'"
          >{{ data.suggestionConfidence || '?' }}</span>
          <SogIcon
            v-show="data.ignore==='Y'"
            icon="mdi-close"
            class="cl"
          />
        </template>
      </ColumnDirective>
      <ColumnDirective
        field="insuranceNumber"
        header-text="VR.Nummer"
        :auto-fit="true"
      />
      <ColumnDirective
        field="evb"
        header-text="eVB/Form.Nr."
        :auto-fit="true"
      />
      <ColumnDirective
        field="versicherungsScheinnummer"
        header-text="GDV-VSNr"
        :auto-fit="true"
      />
      <ColumnDirective
        field="versicherungsBestaetigungsNummer"
        header-text="GDV-VBNr"
        :auto-fit="true"
      />
      <ColumnDirective
        field="datumVersicherungsBeginn"
        header-text="VR-Beginn"
        :auto-fit="true"
        format="dd.MM.yyyy"
        type="dateTime"
      />
      <ColumnDirective
        field="fahrzeugIdentifizierungsNummer"
        header-text="Fahrzeug Id-Nummer"
        :auto-fit="true"
      />
      <ColumnDirective
        field="kennzeichen"
        header-text="Kennzeichnen"
        :auto-fit="true"
      />
      <ColumnDirective
        field="kennzeichenDatumGueltigBis"
        header-text="gueltig Bis"
        :auto-fit="true"
        format="dd.MM.yyyy"
        type="dateTime"
      />
      <!--
            <ColumnDirective
              field="fahrzeugArt"
              header-text="Fahrzeugart"
              :auto-fit="true"
            />
      -->
      <ColumnDirective
        field="kennzeichenZeitpunktZuteilung"
        header-text="Zeitpunkt der Zuteilung"
        :auto-fit="true"
        format="dd.MM.yyyy HH:mm:ss"
        type="dateTime"
      />
      <ColumnDirective
        field="halterVorname"
        header-text="Halter"
        :auto-fit="true"
        :value-accessor="getFullName"
      />
      <ColumnDirective
        field="halterStrasse"
        header-text="Halteranschrift"
        :auto-fit="true"
        :value-accessor="getFullAddress"
      />
    </ColumnsDirective>
  </GridComponent>
</template>

<script setup lang="ts">
import {ref} from "vue";
import {Api, gridActionFailure} from "@/shared/services/api";

import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective
} from "@syncfusion/ej2-vue-grids";
import SogIcon from "@/shared/ui/SogIcon.vue";
import {CustomAdaptor} from "@/shared/types/CustomAdaptor";
import {DataManager} from "@syncfusion/ej2-data/src/manager";
import {useVehicleRegistrationStore} from "@/modules/VehicleRegistration/store";

const _url = Api.baseUrl + '/VehicleRegistrationList';
const _adaptor = new CustomAdaptor()
const _grid = ref<InstanceType<typeof GridComponent> | null>(null)
const _vehicleStore = useVehicleRegistrationStore()
// Seitengröße und Seite aus dem Store (bleibt beim Wechsel zu den Details und nach dem Neuladen erhalten)
const _pageSettings = {
  pageSize: _vehicleStore.listPageSettings.pageSize,
  currentPage: _vehicleStore.listPageSettings.currentPage,
  pageSizes: ["All", 10, 25, 50, 100]
}

/* eslint-disable @typescript-eslint/no-explicit-any */
function onActionComplete(args: any) {
  if (args.requestType !== "paging") {
    return
  }
  // Seitenwechsel und geänderte Seitengröße melden beide "paging"
  const t_pageSettings = (_grid.value as any)?.ej2Instances?.pageSettings
  if (t_pageSettings?.currentPage) {
    _vehicleStore.listPageSettings.currentPage = t_pageSettings.currentPage
  }
  // "All" ergibt eine Seitengröße über 100; die wird nicht gemerkt
  if ([10, 25, 50, 100].includes(t_pageSettings?.pageSize)) {
    _vehicleStore.listPageSettings.pageSize = t_pageSettings.pageSize
  }
}


const _dataManager1 = new DataManager({
  url: _url,
  adaptor: _adaptor,
  crossDomain: true,
})


const _emit = defineEmits<{ (e: 'selected', vehicleRegistrationId?: string, row?: { insuranceId?: string, ignore?: string }): void }>()

function onRowSelected(args: any) {
  _emit("selected", args.data.id, args.data)
}

function onRowDeselected(args: any) {
  if (!args.isInteracted)
    return
  _emit("selected", undefined)
}

function getFullName(field: string, data: any) {
  if (data['halterVorname'])
    return data['halterVorname'] + ' ' + data['halterNachname'];
  else
    return data['halterNachname'];
}

function getFullAddress(field: string, data: any,) {
  return data['halterStrasse'] + ' ' + data['halterHausnummer'] ?? "" + ' ' + data['halterPostleitzahl'] ?? "" + ' ' + data['halterWohnort'] + ' ' + data['halterStaat'];
}

/** Neu laden. Mit <c>resetPage</c> (Filter geändert) zurück auf Seite 1. */
function refresh(resetPage = false) {
  const t_pageSettings = (_grid.value as any)?.ej2Instances?.pageSettings
  if (resetPage && t_pageSettings && t_pageSettings.currentPage !== 1) {
    // Seitenwechsel lädt die Daten selbst
    t_pageSettings.currentPage = 1
    _vehicleStore.listPageSettings.currentPage = 1
    return
  }
  _grid.value?.refresh()
}

defineExpose({
  adaptor: _adaptor, refresh
})

</script>

<style scoped>
.cl {
  color: #ff0000;
}

.confidence {
  display: inline-block;
  min-width: 18px;
  padding: 0 5px;
  border-radius: 9px;
  text-align: center;
  font-size: 0.8em;
  font-weight: bold;
  color: #fff;
  background: #757575;
  cursor: help;
}

.confidence-A {
  background: #2e7d32;
}

.confidence-B {
  background: #689f38;
}

.confidence-C {
  background: #f9a825;
  color: #000;
}

.confidence-D {
  background: #d32f2f;
}

</style>
